import { useDispatch } from 'react-redux';
import { NavLink, useLocation } from 'react-router-dom';
import { Select } from 'antd';
import { LogoShieldIcon } from '../../assets/icons';
import { useGetStatesQuery, useGetReviewQueueQuery } from '../../features/platform/platformApi';
import { usePlatformState, setPlatformState } from '../../features/platform/platformStateSlice';

/** Global sections (outside any state). */
const GLOBAL_ITEMS = [
  { to: '/admin/overview', label: 'All states' },
  { to: '/admin/templates', label: 'Template library' },
];

/** Per-state sections (scoped to the selected state). The review-queue count is live. */
const STATE_ITEMS = [
  { to: '/admin/onboarding', label: 'Onboarding' },
  { to: '/admin/departments', label: 'Departments' },
  { to: '/admin/work-queues', label: 'Work queues' },
  { to: '/admin/government', label: 'Government' },
  { to: '/admin/review-queue', label: 'Review queue', badgeKey: 'review' as const },
  { to: '/admin/resource-directory', label: 'Resources' },
];

const STATUS_SUFFIX: Record<string, string> = {
  notstarted: ' (not started)',
  paused: ' (paused)',
};

/** Shared nav-link styling; `small` is used for the indented per-state items. */
const linkClass = (isActive: boolean, small = false) =>
  [
    'flex items-center rounded-lg font-medium no-underline transition-colors',
    small ? 'px-3 py-2 text-[13px]' : 'px-4 py-3 text-sm',
    isActive ? 'bg-brand-shade text-brand' : 'text-ink-secondary hover:bg-brand-shade/60 hover:text-brand',
  ].join(' ');

interface SidebarProps {
  open?: boolean;
  onClose?: () => void;
}

export default function PlatformSidebar({ open = false, onClose }: SidebarProps) {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const activeOf = (to: string) => pathname === to || pathname.startsWith(to + '/');
  const { code: stateCode } = usePlatformState();
  const { data: states = [] } = useGetStatesQuery();
  const { data: pendingReviews = [] } = useGetReviewQueueQuery(
    { code: stateCode, status: 'pending' },
    { skip: !stateCode },
  );

  const stateOptions = states.map((s) => ({
    value: s.code,
    label: `${s.name}${STATUS_SUFFIX[s.status] ?? ''}`,
  }));
  const reviewCount = pendingReviews.length;

  const onStateChange = (code: string) => {
    const match = states.find((s) => s.code === code);
    dispatch(setPlatformState({ code, name: match?.name ?? code }));
  };

  return (
    <>
      {open && (
        <div
          className="fixed inset-x-0 bottom-0 top-16 z-20 bg-black/30 lg:hidden"
          onClick={onClose}
          aria-hidden
        />
      )}
      <aside
        className={[
          'fixed left-0 top-16 z-30 flex h-[calc(100vh-64px)] w-[280px] flex-col gap-5 overflow-y-auto border-r border-line-soft bg-white pl-4 pr-[17px] py-4 transition-transform duration-200 lg:z-10 lg:translate-x-0',
          open ? 'translate-x-0' : '-translate-x-full',
        ].join(' ')}
      >
        {/* Admin profile card + app indicator */}
        <div className="flex flex-col gap-2.5 rounded-xl bg-brand-shade p-3.5">
          <div className="flex items-center gap-3">
            <span className="flex items-center justify-center rounded-lg bg-brand p-2 text-white">
              <LogoShieldIcon className="size-5" />
            </span>
            <div className="flex flex-col">
              <span className="text-xs font-bold leading-4 text-ink">CHUNAAV Admin</span>
              <span className="text-[10px] font-medium uppercase leading-[15px] tracking-[0.5px] text-brand">
                Platform Admin
              </span>
            </div>
          </div>
          <div className="flex gap-1 rounded-lg border border-[#dbe6fb] bg-white p-[3px]">
            <span className="flex-1 rounded-md bg-white px-3 py-1.5 text-center text-xs font-semibold text-ink">
              Platform admin
            </span>
          </div>
        </div>

        {/* Global sections */}
        <nav className="flex flex-col gap-1" aria-label="Sections">
          {GLOBAL_ITEMS.map(({ to, label }) => (
            <NavLink key={to} to={to} onClick={onClose} className={linkClass(activeOf(to))}>
              {label}
            </NavLink>
          ))}
        </nav>

        {/* State selector + per-state sections */}
        <div className="flex flex-col gap-2">
          <label className="px-1 text-[11px] font-bold uppercase tracking-[0.55px] text-ink-secondary">State</label>
          <Select className="w-full" value={stateCode} onChange={onStateChange} options={stateOptions} />
          <nav className="ml-2 flex flex-col gap-0.5 border-l-2 border-line-soft pl-2" aria-label="State sections">
            {STATE_ITEMS.map(({ to, label, badgeKey }) => (
              <NavLink key={to} to={to} onClick={onClose} className={linkClass(activeOf(to), true)}>
                {label}
                {badgeKey === 'review' && reviewCount > 0 ? ` (${reviewCount})` : ''}
              </NavLink>
            ))}
          </nav>
        </div>
      </aside>
    </>
  );
}
